import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import Modal from '../modal';
import Button from '../button';
import Spinner from '../spinner';
import EmptyState from '../empty-state';
import ContactPicker from '../contact-picker';
import AuthService from '../../../services/auth-service';

import { RecentContainer, RecentList, RecentTitle, RecentUser, SpinnerContainer } from './switch-user.css';
import { useQueryClient } from '@tanstack/react-query';

function SwitchUser({ show = false, onClose, current }) {

	const [user, setUser] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const pickerRef = useRef(null);
	const queryClient = useQueryClient();
	const recentUsersCookie = Cookies.get('pixwel.users.recent');
	const recentUsers = recentUsersCookie ? JSON.parse(recentUsersCookie) : [];

	useEffect(() => {
		if(!show) {
			return;
		}
		// Modal mounts asynchronously; wait a tick before focusing the nested input.
		const timer = window.setTimeout(() => {
			const input = pickerRef.current?.querySelector('input');
			input?.focus();
		}, 0);
		return () => window.clearTimeout(timer);
	}, [show]);

	const handleAddToRecent = (user) => {
		let result = [...recentUsers];
		const i = result.findIndex((u) => u.id === user.id);

		if( i > -1 ) {
			result[i].count++;
		}
		else {
			result.push({
				...user,
				count: 1
			});
		}
		Cookies.set('pixwel.users.recent', JSON.stringify(result));
	};

	const handleSwitchUser = async() => {
		setIsLoading(true);
		if(!AuthService.isImpersonating() && current) {
			Cookies.set('pixwel.user.original', JSON.stringify({
				id: current._id || current.id,
				name: current.name,
				emailNormal: current.emailNormal
			}));
		}
		handleAddToRecent(user);
		queryClient.clear();
		await AuthService.become(user.id);
	};

	const handleCancel = () => {
		setUser(null);
		onClose();
	};

	const renderRecent = () => {
		const originalUserCookie = Cookies.get('pixwel.user.original');
		const originalUser = originalUserCookie ? JSON.parse(originalUserCookie) : null;

		// show top 5 recent users, excluding current user
		const list = recentUsers
			.filter((u) => u.id !== current._id && u.id !== (current.id || current._id))
			.sort((a, b) => b.count - a.count)
			.slice(0, 5);

		// always pin the original user at the top when impersonating
		if(originalUser && originalUser.id !== (current._id || current.id)) {
			const idx = list.findIndex((u) => u.id === originalUser.id);
			if(idx > -1) {
				list.splice(idx, 1);
			}
			list.unshift({ ...originalUser, count: 0 });
		}

		return (
			<RecentContainer>
				<RecentTitle>Recent Users</RecentTitle>
				{list.length > 0
					? (
						<RecentList>
							{list.map((u) => (
								<RecentUser key={u.id}>
									<a onClick={() => setUser(u)}>
										<strong>{u.name}</strong>
										<span>{`<${u.emailNormal}>`}</span>
									</a>
								</RecentUser>
							))}
						</RecentList>
					)
					: (
						<EmptyState message="No recent users" />
					)}
			</RecentContainer>
		);
	};

	const renderContent = () => {
		const contacts = user ? [user] : [];

		return (
			<React.Fragment>
				<Modal.Body style={{ paddingTop: '0' }}>
					<React.Fragment>
						<div ref={pickerRef}>
							<ContactPicker
								limit={1}
								allowGroups={false}
								allowEmails={false}
								exclude={[current]}
								contacts={contacts}
								onUpdate={(u) => setUser(u[0])}
							/>
						</div>
						{renderRecent()}
					</React.Fragment>
				</Modal.Body>

				<Modal.Footer>
					<Button
						variant="secondary"
						lighten
						onClick={handleCancel}
					>
						Cancel
					</Button>

					<Button
						onClick={handleSwitchUser}
						disabled={!user}
					>
						Switch to User
					</Button>
				</Modal.Footer>
			</React.Fragment>
		);
	};

	const renderSpinner = () => (
		<Modal.Body>
			<SpinnerContainer>
				<Spinner />
			</SpinnerContainer>
		</Modal.Body>
	);

	return (
		<Modal
			isOpen={show}
			contentLabel="Switch User"
			size={500}
			shouldCloseOnOverlayClick={false}
			onRequestClose={onClose}
		>
			<Modal.Header>
				<h3>{isLoading ? 'Switching' : 'Switch'} User</h3>
			</Modal.Header>

			{isLoading ? renderSpinner() : renderContent()}
		</Modal>
	);
}

SwitchUser.propTypes = {
	show: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	current: PropTypes.object.isRequired
};

export default SwitchUser;
